'use client'

import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { api, UNAUTHORIZED } from '../api/client'
import type { Session, User } from '../api/types'
import { AuthContext } from './useAuth'

/**
 * Knows who is signed in. The sign-in itself is an HttpOnly cookie that the browser keeps and
 * sends automatically – page code never sees it, which keeps it safe from malicious scripts.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [users, setUsers] = useState<User[]>([])
  const [demoTools, setDemoTools] = useState(false)
  const [ready, setReady] = useState(false)

  const reloadUsers = useCallback(async () => {
    setUsers(await api<User[]>('/users').catch(() => []))
  }, [])

  const accept = useCallback(
    (session: Session) => {
      setUser(session.user)
      setDemoTools(session.demoTools)
      if (!session.user.mustChangePassword) reloadUsers()
    },
    [reloadUsers],
  )

  // On first load: are we still signed in from before?
  useEffect(() => {
    api<Session>('/auth/me')
      .then(accept)
      .catch(() => setUser(null))
      .finally(() => setReady(true))
  }, [accept])

  // If the server ever says "not signed in" (session expired, account deactivated), show the sign-in page.
  useEffect(() => {
    const signedOut = () => {
      setUser(null)
      setUsers([])
    }
    window.addEventListener(UNAUTHORIZED, signedOut)
    return () => window.removeEventListener(UNAUTHORIZED, signedOut)
  }, [])

  const signIn = async (email: string, password: string) => {
    accept(await api<Session>('/auth/login', 'POST', { email, password }))
  }

  const signOut = async () => {
    await api('/auth/logout', 'POST').catch(() => {})
    setUser(null)
    setUsers([])
  }

  const updateUser = (updated: User) => {
    setUser(updated)
    if (!updated.mustChangePassword) reloadUsers()
  }

  return (
    <AuthContext.Provider value={{ user, users, demoTools, ready, signIn, signOut, updateUser, reloadUsers }}>
      {children}
    </AuthContext.Provider>
  )
}
